import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { selectPb_email, selectPb_username } from 'store/selectors';
import { globalAPI } from 'services/global.service';
import { Check, DocumentOutline, AppNextLogoMark } from 'assets/icons';
import * as S from './DashboardWelcome.styled';

const DESCRIPTION =
  'Ready to scale your app growth? Complete your account setup to launch targeted ad campaigns effortlessly, or take a quick platform tour to explore new features and reporting tools';

const GETTING_STARTED_LINKS = [
  {
    label: 'Getting Started - Add a New Campaign',
    href: 'https://wiki.appnext.com/hc/en-us/articles/4403988829074-Getting-Started-Add-a-New-Campaign',
  },
  {
    label: 'Get in Touch with our Local Offices',
    href: 'https://wiki.appnext.com/hc/en-us/articles/115000841845-Get-in-Touch-with-our-Local-Offices',
  },
  {
    label: 'Getting Started - Add a New App',
    href: 'https://wiki.appnext.com/hc/en-us/articles/202228001-Getting-Started-Add-a-New-App',
  },
  {
    label: 'Appnext Macros',
    href: 'https://wiki.appnext.com/hc/en-us/articles/4404347716114-Appnext-Macros',
  },
  {
    label: 'How do we count the installs?',
    href: 'https://wiki.appnext.com/hc/en-us/articles/208488975-How-do-we-count-the-installs',
  },
  {
    label: 'What is a Direct-Buy campaign?',
    href: 'https://wiki.appnext.com/hc/en-us/articles/203321171-What-is-a-Direct-Buy-campaign',
  },
  {
    label: 'What are the tracking solutions supported by Appnext?',
    href: 'https://wiki.appnext.com/hc/en-us/articles/203322281-What-are-the-tracking-solutions-supported-by-Appnext',
  },
];

const SETUP_STEPS = [
  {
    key: 'company_profile',
    title: 'Fill Company Profile',
    description: 'Add your company and contact details to verify and secure your account.',
    cta: 'Fill Company Profile',
  },
  {
    key: 'payment_methods',
    title: 'Payment Methods',
    description:
      'Your service will start immediately after adding your payment method, and you pay after you accrue costs.',
    cta: 'Add Payment Methods',
  },
  {
    key: 'first_campaign',
    title: 'First Campaign',
    description: 'Create your first ad campaign to promote your app.',
    cta: 'Create Campaign',
  },
  {
    key: 'set_budget',
    title: 'Set the Budget',
    description: '',
    cta: 'Set the Budget',
  },
];

// Frontend owns routing: resolve the API's stable actionKey to an internal route.
const ACTION_ROUTES = {
  company_profile: { to: '/account-settings' },
  payment_methods: { to: '/payments' },
  first_campaign: { to: '/Discovery/Campaign/New' },
  // `openAppsBudgets` tells the Manage page to open the Apps Budgets modal on arrival.
  set_budget: { to: '/Discovery/Manage', state: { openAppsBudgets: true } },
};

const getInitials = (username, email) => {
  const source = username || email || '';
  const words = source
    .trim()
    .split(/[\s@._-]+/)
    .filter(Boolean);
  if (!words.length) return '';
  const initials = words.slice(0, 2).map((word) => word[0]);
  return initials.join('').toUpperCase();
};

export const DashboardWelcome = () => {
  const navigate = useNavigate();
  const username = useSelector(selectPb_username);
  const email = useSelector(selectPb_email);

  const { data, isLoading, isError } = globalAPI.useGetSetupStepsQuery();

  const completedCount = data
    ? Object?.values(data)?.filter((status) => status === 'completed').length
    : 0;

  const firstIncompleteStep = data
    ? SETUP_STEPS.find((step) => data[step.key] !== 'completed')
    : null;

  const handleStepClick = (route) => {
    if (route) navigate(route.to, { state: route.state });
  };

  const initials = getInitials(username, email);
  const displayName = username || '';

  const renderSetup = () => {
    if (isLoading) {
      return <S.StatusText>Loading setup…</S.StatusText>;
    }
    if (isError) {
      return null;
    }

    return (
      <>
        <S.ProgressSection>
          <S.ProgressTitle>
            <strong>Complete</strong>
            <span> Setup</span>
          </S.ProgressTitle>
          <S.ProgressBar>
            {SETUP_STEPS.map((step, index) => (
              <S.ProgressSegment key={step.key} $filled={index < completedCount} />
            ))}
          </S.ProgressBar>
        </S.ProgressSection>

        <S.StepList>
          {SETUP_STEPS.map((step, index) => {
            const state = data[step.key] || 'pending';
            const isActive = step === firstIncompleteStep;
            const route = step.cta ? ACTION_ROUTES[step.key] : undefined;

            return (
              <S.Step key={step.key} $state={state}>
                <S.StepRow $align={step.description && isActive ? 'flex-start' : 'center'}>
                  {state === 'completed' ? (
                    <S.StepDoneBadge>
                      <Check />
                    </S.StepDoneBadge>
                  ) : (
                    <S.StepNumber $state={state}>{index + 1}</S.StepNumber>
                  )}
                  <S.StepContent>
                    <S.StepLabel $muted={!isActive}>{step.title}</S.StepLabel>
                    {step.description && isActive && (
                      <S.StepDescription>{step.description}</S.StepDescription>
                    )}
                  </S.StepContent>
                </S.StepRow>
                {step.cta && isActive && (
                  <S.StepButton type='button' onClick={() => handleStepClick(route)}>
                    {step.cta}
                  </S.StepButton>
                )}
              </S.Step>
            );
          })}
        </S.StepList>
      </>
    );
  };

  return (
    <S.Wrap>
      <S.Left>
        <S.BannerWrap>
          <S.Banner>
            <S.BannerContent>
              <S.UserBlock>
                <S.UserName>Welcome, {displayName}!</S.UserName>
                {email && <S.UserEmail>{email}</S.UserEmail>}
              </S.UserBlock>
              <S.Description>{DESCRIPTION}</S.Description>
              {/* <S.PrimaryButton type='button'>START CAMPAIGN WITH AI CHAT</S.PrimaryButton> */}
            </S.BannerContent>

            <S.BannerBadge>
              <S.BadgeLogo>
                <AppNextLogoMark />
              </S.BadgeLogo>
              {initials && <S.Avatar>{initials}</S.Avatar>}
            </S.BannerBadge>
          </S.Banner>
        </S.BannerWrap>

        <S.GettingStarted>
          <S.SectionHeading>
            <S.SectionTitle>Getting started</S.SectionTitle>
            <S.SectionSubtitle>Everything you need to start working with Appnext</S.SectionSubtitle>
          </S.SectionHeading>
          <S.LinkList>
            {GETTING_STARTED_LINKS.map((link) => (
              <S.LinkItem
                key={link.label}
                href={link.href}
                target='_blank'
                rel='noopener noreferrer'
              >
                <DocumentOutline />
                {link.label}
              </S.LinkItem>
            ))}
          </S.LinkList>
        </S.GettingStarted>
      </S.Left>

      <S.Divider />

      <S.Right>{renderSetup()}</S.Right>
    </S.Wrap>
  );
};
